import { useMemo, type ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Switch from "@/v2-ui/switch"
import Button, { TooltipButton } from "@/v2-backoffice-client/components/button"
import { useTableContext } from "./table.context"
import { getColumnTitle } from "./table.utils"

export type TableToolbarColumnsPanelProps = ComponentProps<"div"> & {
  onClose(): void
}

function TableToolbarColumnsPanel(props: TableToolbarColumnsPanelProps) {
  const { onClose, className, ...divProps } = props
  const {
    table,
    defaultColumnVisibility,
    updateUserColumnVisibility,
    clearUserColumnVisibility,
  } = useTableContext()

  const cols = useMemo(() => {
    return table.getAllLeafColumns().filter((c) => {
      if (c.id.startsWith("__")) return false
      return c.getCanHide()
    })
  }, [table])

  return (
    <div className={cn("ui-table-toolbar-panel", className)} {...divProps}>
      <div className="ui-table-toolbar-panel-header">
        <span>Toggle columns</span>

        <Button
          variant="ghost"
          size="xs"
          onClick={() => {
            const update: Record<string, boolean> = {}
            for (const c of cols) {
              c.toggleVisibility(true)
              update[c.id] = true
            }
            updateUserColumnVisibility(update)
          }}
        >
          Show all
        </Button>

        <Button
          variant="ghost"
          size="xs"
          onClick={() => {
            const update: Record<string, boolean> = {}
            for (const c of cols) {
              c.toggleVisibility(false)
              update[c.id] = false
            }
            updateUserColumnVisibility(update)
          }}
        >
          Hide all
        </Button>

        <Button
          variant="ghost"
          size="xs"
          onClick={() => {
            clearUserColumnVisibility()
            table.setColumnVisibility(defaultColumnVisibility)
          }}
        >
          Reset
        </Button>

        <div className="ui-table-toolbar-panel-header-spacer" />

        <TooltipButton
          variant="ghost"
          size="xs"
          iconSrc="cross-small"
          iconClassName="ui-table-panel-close-icon"
          label="Close the columns panel"
          onClick={onClose}
        />
      </div>

      <div className="ui-table-toolbar-panel-body">
        {cols.map((c) => (
          <div key={c.id} className="ui-table-toolbar-panel-item">
            <Switch
              size="xs"
              checked={c.getIsVisible()}
              onCheckedChange={(checked) => {
                c.toggleVisibility(checked)
                updateUserColumnVisibility({ [c.id]: checked })
              }}
            />
            <span className="ui-table-toolbar-panel-item-label">
              {getColumnTitle(c)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default TableToolbarColumnsPanel
